import { getCurrentUser } from '@bridebook/toolbox/src/api/auth/getCurrentUser';
import { HttpStatusCode } from '@bridebook/toolbox/src/api/http-status';

/**
 * An endpoint _**[query, body, response]**_ payload definition.
 */
export type EndpointPayload = [any, any, any];

/**
 * An endpoint definition supporting multiple HTTP methods.
 */
type EndpointDefinition = {
  [M in HttpMethod]?: EndpointPayload;
};

/**
 * A map of endpoint definitions.
 */
type EndpointsMap = {
  [E in string]: EndpointDefinition;
};

/**
 * Get all endpoints that support a specific method.
 */
type EndpointsByMethod<Endpoints extends EndpointsMap, M extends HttpMethod> = {
  [E in keyof Endpoints]: M extends keyof Endpoints[E]
    ? Endpoints[E][M] extends EndpointPayload
      ? E
      : never
    : never;
}[keyof Endpoints];

/**
 * Extract types from `[param]` notation endpoints.
 */
type ExtractBracketParam<T extends string> = T extends `${string}[${infer Param}]${infer Rest}`
  ? { [K in Param | keyof ExtractBracketParam<Rest>]: StringLike }
  : object;

/**
 * Extract types from `:param` notation endpoints.
 */
type ExtractColonParam<T extends string> = T extends `${string}:${infer Param}/${infer Rest}`
  ? { [K in Param | keyof ExtractColonParam<`/${Rest}`>]: string }
  : T extends `${string}:${infer Param}`
    ? { [K in Param]: StringLike }
    : object;

/**
 * Extract the payload for a specific endpoint and method.
 */
type ExtractPayload<
  Endpoints extends EndpointsMap,
  E extends keyof Endpoints,
  M extends HttpMethod,
> = Endpoints[E] extends { [K in M]: infer T } ? (T extends EndpointPayload ? T : never) : never;

/**
 * Extract route parameters from an endpoint.
 */
type ExtractRouteParams<T extends string> = ExtractColonParam<T> & ExtractBracketParam<T>;

/**
 * Common HTTP methods.
 */
type HttpMethod = ('GET' | 'HEAD') | ('DELETE' | 'PATCH' | 'POST' | 'PUT');

/**
 * This utility type checks if a given type is entirely optional.
 */
type IsAllOptional<T> = Record<string, never> extends T ? true : false;

/**
 * If all properties in T are optional, make T accept undefined.
 */
type NilIfAllOptional<T> = IsAllOptional<T> extends true ? T | null : T;

/**
 * Default query parameters payload type.
 */
type PayloadQuery = Record<string, undefined | null | StringLike | StringLike[]>;

/**
 * Primitives that have a `toString` method implementation.
 */
type StringLike = bigint | boolean | number | string;

/**
 * Goals:
 *
 * 1. Offer typed requests and responses, and a centralized place to manage them.
 * 2. Abstract need for authenticated variants.
 * 3. Consistent handling of errors.
 * 4. Abstract away from fetch, node-fetch, isomorphic-fetch, axios, got, undici, etc...
 * 5. Offer telemetry.
 * 6. Offer caching and duplicate requests resolution.
 */
export class Fetchy<Endpoints extends { [K in keyof Endpoints]: EndpointDefinition }> {
  #auth: boolean | string = false;
  #base = 'http://localhost';
  #headers = new Headers({
    Accept: 'application/json',
  });

  #options?: RequestInit;

  /**
   * @param base The base URL for the current instance, optional.
   */
  constructor(base?: string) {
    if (base != null) {
      this.#base = base;
    }
  }

  private async request<T>(
    method: HttpMethod,
    endpoint: string,
    query?: null | PayloadQuery,
    payload?: unknown,
  ): Promise<T> {
    const url = new URL(endpoint, this.#base);
    const pattern = /(?<=[/])(?:[:[]([^?\]/]+)]?)(?=[/?]|$)/g;

    /**
     * Replace all route parameters with values from the query object.
     * If a route parameter is missing, throws an error.
     * If a parameter is not used for the route, it will be considered as a query string parameter.
     */
    url.pathname = url.pathname.replace(pattern, (_, parameter: string) => {
      const value = query?.[parameter];

      if (value != undefined) {
        delete query?.[parameter];
      } else {
        throw new FetchyError(`Missing parameter: '${parameter}'.`, method, endpoint);
      }

      return value as string;
    });

    if (query != null) {
      for (const key in query) {
        if (Array.isArray(query[key])) {
          for (const value of query[key] as StringLike[]) {
            url.searchParams.append(key, value.toString());
          }
        } else if (query[key] != null) {
          url.searchParams.set(key, (query[key] as StringLike).toString());
        }
      }
    }

    const options: RequestInit & { headers: Headers } = {
      ...(this.#options ?? {}),
      method,
      headers: this.#headers,
    };

    for (const [key, value] of Object.entries(this.#options?.headers ?? {})) {
      options.headers.set(key, value);
    }

    if (this.#auth !== false) {
      if (this.#auth === true) {
        const currentUser = await getCurrentUser();

        if (currentUser != null) {
          this.#headers.set('Authorization', 'Bearer ' + (await currentUser.getIdToken()));
        }
      } else {
        const match = this.#auth.match(/^(?<scheme>Basic|Bearer)\s+(?<token>[-_./+~0-9a-z]+=*)$/i);

        if (match?.groups != null) {
          this.#headers.set('Authorization', `${match.groups.scheme} ${match.groups.token}`);
        } else {
          this.#headers.set('Authorization', 'Bearer ' + this.#auth);
        }
      }
    }

    if (['GET', 'HEAD'].includes(method) !== true) {
      options.headers.set('Content-Type', 'application/json');

      if (payload != null) {
        options.body = JSON.stringify(payload);
      }
    }

    const response = await fetch(
      url.origin === 'http://localhost' ? [url.pathname, url.search].join('') : url.toString(),
      options,
    );

    if (response.ok !== true) {
      if (response.status >= 500) {
        throw new FetchyError(
          response.statusText,
          method,
          endpoint,
          response,
          await response.text(),
        );
      }

      if (response.status >= 400) {
        throw new FetchyError(
          response.statusText,
          method,
          endpoint,
          response,
          await response.text(),
        );
      }

      throw new FetchyError('Unknown Error', method, endpoint, response, await response.text());
    }

    try {
      if (method === 'HEAD') {
        return Object.fromEntries<string>(Object.entries(response?.headers ?? {})) as T;
      }

      return (await response.json()) as T;
    } catch {
      throw new FetchyError('Failed to parse JSON response.', method, endpoint, response);
    }
  }

  /**
   * 🔒 Sets the authorization for the current request, using the current user's ID token.
   */
  public get authenticated() {
    return this.authorized();
  }

  /**
   * 🔐 Sets the authorization for the current request.
   * Accepts either a scheme + token string or just a raw token (defaulting to `Bearer`).
   * If no bearer token is provided, the method will default to using the current user's ID token.
   *
   * @param bearer The bearer token to use instead of the current user's ID token.
   */
  public authorized(token?: boolean | string) {
    if (token == null || typeof token === 'boolean') {
      this.#auth = token == null ? true : token;
    } else {
      this.#auth = token.trim();
    }

    return this;
  }

  /**
   * 📥 Sends a **strongly-typed** GET request to the specified endpoint with optional query parameters.
   */
  public async get<
    E extends EndpointsByMethod<Endpoints, 'GET'> & string,
    R extends ExtractPayload<Endpoints, E, 'GET'>,
  >(
    endpoint: E,
    query: NilIfAllOptional<ExtractRouteParams<E> & PayloadQuery & R[0]>,
  ): Promise<R[2]> {
    return this.request('GET', endpoint, query, null);
  }

  /**
   * 👀 Sends a **weakly-typed** HEAD request to the specified endpoint with optional query parameters.
   */
  public async head<
    E extends EndpointsByMethod<Endpoints, 'GET'> & string,
    R extends ExtractPayload<Endpoints, E, 'GET'>,
  >(
    endpoint: E,
    query: NilIfAllOptional<ExtractRouteParams<E> & PayloadQuery & R[0]>,
  ): Promise<Record<string, string>> {
    return this.request('HEAD', endpoint, query, null);
  }

  /**
   * 🗑️ Sends a **strongly-typed** DELETE request to the specified endpoint with optional query parameters and payload.
   */
  public async delete<
    E extends EndpointsByMethod<Endpoints, 'DELETE'> & string,
    R extends ExtractPayload<Endpoints, E, 'DELETE'>,
  >(
    endpoint: E,
    query: NilIfAllOptional<ExtractRouteParams<E> & PayloadQuery & R[0]>,
    payload: NilIfAllOptional<R[1]>,
  ): Promise<R[2]> {
    return this.request('DELETE', endpoint, query, payload);
  }

  /**
   * 🩹 Sends a **strongly-typed** PATCH request to the specified endpoint with optional query parameters and payload.
   */
  public async patch<
    E extends EndpointsByMethod<Endpoints, 'PATCH'> & string,
    R extends ExtractPayload<Endpoints, E, 'PATCH'>,
  >(
    endpoint: E,
    query: NilIfAllOptional<ExtractRouteParams<E> & PayloadQuery & R[0]>,
    payload: NilIfAllOptional<R[1]>,
  ): Promise<R[2]> {
    return this.request('PATCH', endpoint, query, payload);
  }

  /**
   * ➕ Sends a **strongly-typed** POST request to the specified endpoint with optional query parameters and payload.
   */
  public async post<
    E extends EndpointsByMethod<Endpoints, 'POST'> & string,
    R extends ExtractPayload<Endpoints, E, 'POST'>,
  >(
    endpoint: E,
    query: NilIfAllOptional<ExtractRouteParams<E> & PayloadQuery & R[0]>,
    payload: NilIfAllOptional<R[1]>,
  ): Promise<R[2]> {
    return this.request('POST', endpoint, query, payload);
  }

  /**
   * 📝 Sends a **strongly-typed** PUT request to the specified endpoint with optional query parameters and payload.
   */
  public async put<
    E extends EndpointsByMethod<Endpoints, 'PUT'> & string,
    R extends ExtractPayload<Endpoints, E, 'PUT'>,
  >(
    endpoint: E,
    query: NilIfAllOptional<ExtractRouteParams<E> & PayloadQuery & R[0]>,
    payload: NilIfAllOptional<R[1]>,
  ): Promise<R[2]> {
    return this.request('PUT', endpoint, query, payload);
  }

  /**
   * 🔧 Sets additional request options for the current instance.
   */
  public with(options: Partial<RequestInit>) {
    if (Object.keys(options).length > 0) {
      this.#options = options;
    }

    return this;
  }
}

export class FetchyError extends Error {
  #body?: string;
  #method: HttpMethod;
  #endpoint: string;
  #response?: Response;

  /**
   * @param message Error message.
   * @param method The HTTP method used for the request that caused the error.
   * @param endpoint The endpoint that caused the error.
   * @param response The full Response object from the server, if available.
   * @param body The JSON or plain text response from the server, if available.
   */
  constructor(
    message: string,
    method: HttpMethod,
    endpoint: string,
    response?: Response,
    body?: string,
  ) {
    super(message);

    if (Error.captureStackTrace != null) {
      Error.captureStackTrace(this, FetchyError);
    }

    this.name = 'FetchyError';

    this.#body = body;
    this.#method = method;
    this.#endpoint = endpoint;
    this.#response = response;
  }

  /**
   * The JSON or plain text response from the server, if available.
   */
  get body(): Record<string, unknown> | string | undefined {
    if (this.#body != null) {
      try {
        return JSON.parse(this.#body);
      } catch {
        return this.#body;
      }
    }

    return this.#body;
  }

  /**
   * The HTTP status code of the response, if available.
   */
  get code() {
    return this.#response?.status ?? HttpStatusCode.InternalServerError;
  }

  /**
   * The HTTP method used for the request that caused the error.
   */
  get method() {
    return this.#method;
  }

  /**
   * The endpoint that caused the error.
   */
  get endpoint() {
    return this.#endpoint;
  }

  /**
   * The headers of the response from the server, if available.
   */
  get headers() {
    return Object.fromEntries<string>(Object.entries(this.#response?.headers ?? {}));
  }

  /**
   * The full `Response` object from the server, if available.
   */
  get response() {
    return this.#response;
  }

  /**
   * String representation of the FetchyError.
   */
  toString() {
    return `FetchyError: ${this.#method} ${this.endpoint} → ${this.code} (${this.message})`;
  }
}

/**
 * Returns a new **untyped** instance of Fetchy.
 *
 * @warning This instance is untyped, prefer workspace variants instead.
 *
 * @param base The base URL for the current instance, optional.
 */
export function fetchy(base?: string) {
  return new Fetchy<{ [k: string]: Required<EndpointDefinition> }>(base);
}
